import React, { useEffect, useState } from "react";
import "../Styling/balancesheet.css";
import axios from "axios";
import { Base_URL } from "../url";
import { useAuth } from "../Context/AuthContext";
import { jsPDF } from "jspdf";
import "jspdf-autotable";

const BalanceSheetReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  
  // Filter states - organisationId is fixed to user's organisation
  const [filters, setFilters] = useState({
    organisationId: user?.organisationId || "",
    companyCode: "", // Starts empty
    fiscalYear: new Date().getFullYear(),
    fiscalMonth: "",
  });
  
  const [organisations, setOrganisations] = useState([]);
  const [companyCodes, setCompanyCodes] = useState([]);
  const [fiscalYears, setFiscalYears] = useState([]);

  // 1. On component mount, set organisationId from user context
  useEffect(() => {
    if (user?.organisationId) {
      setFilters(prev => ({
        ...prev,
        organisationId: user.organisationId
      }));
      setLoading(true); // Start loading while fetching codes
    }
  }, [user]);

  // 2. Fetch organisations for dropdown
  useEffect(() => {
    const fetchOrganisations = async () => {
      try {
        const response = await axios.get(
          `${Base_URL}/Balancesheet/bs-organisations-with-names`,
          { headers: { Authorization: `Bearer ${user?.token}` } }
        );
        setOrganisations(response.data);
      } catch (error) {
        console.error("Error fetching organisations:", error);
      }
    };
    
    if (user?.token) fetchOrganisations();
  }, [user]);


// 🎯 FIX: Fetch company codes AND automatically set the default companyCode filter
useEffect(() => {
  // console.log(user.organisationId)
  if (!user?.token || !user?.organisationId) {
    setLoading(true);
    
    // return;
  }


  const fetchCompanyCodes = async () => {
    try {
      const url =`${Base_URL}/Balancesheet/bs-company-codes-with-names/${user?.organisationId}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      console.log(response);

      const fetchedCodes = response.data;
      setCompanyCodes(fetchedCodes);

      // ** AUTO-SELECTION LOGIC **
      if (fetchedCodes && fetchedCodes.length > 0) {
        setFilters(prev => {
          // Set the first code only if nothing is currently selected
          if (!prev.companyCode) {
            return {
              ...prev,
              companyCode: fetchedCodes[0].companyCode
            };
          }
          return prev;
        });
      }
      
    } catch (error) {
      console.error("Error fetching company codes:", error.response?.data || error.message);
      setCompanyCodes([]);
    }
    // Note: Loading will be set to false in the next effect when data is fetched or fails.
  };

  fetchCompanyCodes();
}, [user?.token, user?.organisationId]);

  // 3. Generate fiscal years (current year and previous 5 years)
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    // Generate years from 5 years ago up to the current year
    for (let i = 5; i >= 0; i--) {
      years.push(currentYear - i);
    }
    setFiscalYears(years);
  }, []);

  // 4. Fetch balance sheet data when filters change
  useEffect(() => {
    const fetchBalanceSheet = async () => {
      // Data fetch will now be triggered automatically after companyCode is set in the previous useEffect
      if (!user?.organisationId || !filters.companyCode) {
        setData(null);
        setLoading(false); // Stop loading if filters are incomplete
        return;
      }

      try {
        setLoading(true);
        
        let url = `${Base_URL}/Balancesheet/balance-sheet/${user.organisationId}/${filters.companyCode}/${filters.fiscalYear}`;
        
        if (filters.fiscalMonth) {
          url += `/${filters.fiscalMonth}`;
        }
        
        console.log("Calling URL for Balance Sheet:", url);

        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        });
        setData(response.data);
      } catch (error) {
        console.error("Error fetching balance sheet:", error);
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    // Use a short debounce to avoid firing multiple times if filters change rapidly
    const timer = setTimeout(() => {
      if (filters.companyCode) {
        fetchBalanceSheet();
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [filters, user]);

  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Get current organisation name
  const getCurrentOrganisationName = () => {
    if (!user?.organisationId) return "";
    const org = organisations.find(o => o.organisationId == user.organisationId); 
    return org ? org.organisationName : user.organisationId;
  };

  // Generate PDF
  const generatePDF = () => {
    if (!data) return;
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    
    // Header
    doc.setFontSize(16);
    doc.setTextColor(26, 61, 124);
    doc.text(data.companyName, pageWidth / 2, 15, { align: "center" });
    
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(data.companyAddress, pageWidth / 2, 22, { align: "center" });
    
    doc.setFontSize(14);
    doc.setTextColor(26, 61, 124);
    
    // Dynamic title
    let reportTitle = `Balance Sheet - ${filters.fiscalYear}`;
    if (filters.fiscalMonth) {
      const monthNames = ["January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"];
      reportTitle = `Balance Sheet - ${monthNames[parseInt(filters.fiscalMonth) - 1]} ${filters.fiscalYear}`;
    }
    
    doc.text(reportTitle, pageWidth / 2, 30, { align: "center" });

    // Helper for formatting category name
    const formatCategoryNameForPdf = (name) => {
        if (!name) return "Uncategorized";
        // Convert camelCase or PascalCase to "Title Case"
        return name.replace(/([A-Z])/g, " $1").trim().replace(/^./, str => str.toUpperCase());
    };
    
    // Helper for formatting amount
    const formatAmountForPdf = (amount) => {
        const num = Number(amount || 0);
        return num.toLocaleString('en-IN', { minimumFractionDigits: 2 });
    };

    // Assets Table
    doc.autoTable({
      startY: 40,
      head: [["Assets", "Amount (₹)"]],
      body: data.assetSummary?.categories?.map(item => [
        formatCategoryNameForPdf(item.category),
        formatAmountForPdf(item.amount)
      ]) || data.assets?.map(item => [
        formatCategoryNameForPdf(item.name || item.category),
        formatAmountForPdf(item.amount)
      ]) || [],
      headStyles: { fillColor: [26, 61, 124] },
      margin: { horizontal: 10 },
      columnStyles: {
        1: { halign: 'right' }
      }
    });

    // Liabilities Table
    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 10,
      head: [["Liabilities & Equity", "Amount (₹)"]],
      body: data.liabilitySummary?.categories?.map(item => [
        formatCategoryNameForPdf(item.category),
        formatAmountForPdf(item.amount)
      ]) || data.liabilities?.map(item => [
        formatCategoryNameForPdf(item.name || item.category),
        formatAmountForPdf(item.amount)
      ]) || [],
      headStyles: { fillColor: [52, 152, 219] },
      margin: { horizontal: 10 },
      columnStyles: {
        1: { halign: 'right' }
      }
    });

    doc.save(`balance-sheet-${filters.companyCode}-${filters.fiscalYear}${filters.fiscalMonth ? '-month-' + filters.fiscalMonth : ''}.pdf`);
  };

  const formatCategoryName = (name) => {
    if (!name) return "Uncategorized";
    return name.replace(/([A-Z])/g, " $1").trim();
  };

  // Month options
  const monthOptions = [
    { value: "", label: "All Months (Yearly)" },
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  return (
    <div className="bsr-wrapper">
      {/* Filter Section */}
      <div className="bsr-filters">
        <h3>Balance Sheet Filters</h3>
        <div className="bsr-filter-grid">
  {/* Organisation ID - Fixed/Readonly */}
  <div className="bsr-filter-group">
    <label>Organisation</label>
    <div className="bsr-fixed-field">
      {getCurrentOrganisationName()} (ID: {user?.organisationId})
    </div>
  </div>

  <div className="bsr-filter-group">
    <label>Company Code *</label>
    <select
      name="companyCode"
      value={filters.companyCode}
      onChange={handleFilterChange}
      required
      // Disable if loading or if no codes are available
      disabled={loading && companyCodes.length === 0} 
    >
      <option value="">Select Company Code</option>
      {companyCodes.map((company) => (
        <option key={company.companyCode} value={company.companyCode}>
          {company.companyCode} - {company.companyName}
        </option>
      ))}
    </select>
    {loading && companyCodes.length === 0 && <small className="bsr-filter-warning">Fetching codes...</small>}
  </div>

  <div className="bsr-filter-group">
    <label>Fiscal Year *</label>
    <select
      name="fiscalYear"
      value={filters.fiscalYear}
      onChange={handleFilterChange}
      required
    >
      {fiscalYears.map((year) => (
        <option key={year} value={year}>
          {year}
        </option>
      ))}
    </select>
  </div>

  <div className="bsr-filter-group">
    <label>Fiscal Month</label>
    <select
      name="fiscalMonth"
      value={filters.fiscalMonth}
      onChange={handleFilterChange}
    >
      {monthOptions.map((month) => (
        <option key={month.value} value={month.value}>
          {month.label}
        </option>
      ))}
    </select>
  </div>
</div>

        {filters.companyCode && (
          <div className="bsr-active-filters">
            <small>
              <strong>Active Filters:</strong>
              {` Organisation: ${getCurrentOrganisationName()} | Company: ${filters.companyCode} | Year: ${filters.fiscalYear}`}
              {filters.fiscalMonth && ` | Month: ${monthOptions.find(m => m.value === filters.fiscalMonth)?.label}`}
              {loading && " | Loading..."}
            </small>
          </div>
        )}
      </div>

      {/* Report Header with PDF button */}
      {data && !loading && (
        <div className="bsr-header">
          <div className="bsr-pdf-button">
            <button onClick={generatePDF} className="bsr-fpdf-btn">
              Download PDF
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="bsr-loading">
          Loading Balance Sheet...
        </div>
      )}

      {/* Main Content */}
      {!loading && data && (
        <div className="bsr-container">
          {/* Company Info */}
          <div className="bsr-company-header">
            {data.logoUrl && data.logoUrl !== "No Logo Available" && (
              <img src={data.logoUrl} alt="Logo" className="bsr-logo" />
            )}
            <div className="bsr-company-text">
              <h2>{data.companyName}</h2>
              <p className="bsr-company-address">{data.companyAddress}</p>
            </div>
          </div>

          {/* Report Title */}
          <div className="bsr-report-title">
            <h1>Balance Sheet</h1>
            <p className="bsr-year">
              {data.asOfDate || `As of December 31, ${filters.fiscalYear}`}
            </p>
            <p className="bsr-company-code">Company Code: {data.companyCode || filters.companyCode}</p>
             
          </div>

          {/* Assets Section */}
          <div className="bsr-section">
            <h3 className="bsr-section-title">Assets</h3>
            <table className="bsr-table">
              <thead>
                <tr>
                  <th>Particulars</th>
                  <th>Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                {(data.assetSummary?.categories || data.assets)?.map((item) => (
                  <tr key={item.category || item.name}>
                    <td>{formatCategoryName(item.category || item.name)}</td>
                    <td className="bsr-amount">
                      {item.amount.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })}
                    </td>
                  </tr>
                ))}
                {(!data.assetSummary?.categories && !data.assets) && (
                  <tr>
                    <td colSpan="2">No asset data available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Liabilities Section */}
          <div className="bsr-section">
            <h3 className="bsr-section-title">Liabilities & Equity</h3>
            <table className="bsr-table">
              <thead>
                <tr>
                  <th>Particulars</th>
                  <th>Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                {(data.liabilitySummary?.categories || data.liabilities)?.map((item) => (
                  <tr key={item.category || item.name}>
                    <td>{formatCategoryName(item.category || item.name)}</td>
                    <td className="bsr-amount">
                      {item.amount.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })}
                    </td>
                  </tr>
                ))}
                {(!data.liabilitySummary?.categories && !data.liabilities) && (
                  <tr>
                    <td colSpan="2">No liability data available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="bsr-totals">
            <div className="bsr-total-item bsr-total-asset-row">
              <span>Total Assets:</span>
              <span className="bsr-total-amount">
                ₹{(data.assetSummary?.total || data.totalAssets || 0).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                })}
              </span>
            </div>
            <div className="bsr-total-item bsr-total-liability-row">
              <span>Total Liabilities & Equity:</span> 
              <span className="bsr-total-amount">
                ₹{(data.liabilitySummary?.total || data.totalLiabilities || 0).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                })}
              </span>
            </div>
          </div>

          {/* Footer */}
          <div className="bsr-footer">
            <p>Generated on: {new Date().toLocaleDateString()}</p>
            <p>Organisation: {getCurrentOrganisationName()}</p>
          </div>
        </div>
      )}

      {/* No Data State */}
      {!loading && !data && filters.companyCode && (
        <div className="bsr-no-data">
          <p>No data available for the selected company code (**{filters.companyCode}**) and filters.</p>
        </div>
      )}

      {/* Initial State (Only shown if company codes are still loading or unavailable) */}
      {!loading && !data && !filters.companyCode && (
        <div className="bsr-initial-state">
          <p>
            {companyCodes.length === 0 ? 
            "Waiting for Company Codes or Report Data..." : 
            "Please select a company code to generate the Balance Sheet report."}
          </p>
        </div>
      )}
    </div>
  );
};

export default BalanceSheetReport;